import { useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { CONNECTOR_LABEL, useConnection } from '@/entities/connection';
import { scoreBand } from '@/features/scoring';
import { useCursorStack } from '@/shared/api/paging';
import type { ScoreSummary } from '@/shared/api/generated/scoring';
import { ageText } from '@/shared/model/age';
import { compactNumber } from '@/shared/model/number';
import { SCREEN_PATHS } from '@/shared/routes';
import { Alert } from '@/shared/ui/Alert';
import { DatasetObjectTypeIcon } from '@/shared/ui/DatasetObjectTypeIcon';
import { PageHeader } from '@/shared/ui/PageHeader';
import { SkeletonRows } from '@/shared/ui/Skeleton';

import { DATASETS_PER_PAGE, usePagedDatasets } from '../api/catalog';
import { useConnectionSummaries, useDatasetFacts } from '../api/catalogue';
import { useLatestScores } from '../api/scores';
import { profileReading } from '../model/catalogue';
import type { ProfileReading } from '../model/catalogue';
import { BulkAnalysisControl } from './BulkAnalysisControl';

/* One source's catalogue: the tables a harvest of it discovered, as the design's grouped table.
 *
 * **One card for the source, its rows beneath**, each row a way into the dataset: the whole row is
 * the link's target (a stretched `::after`), and the name is the link itself, so a keyboard reaches
 * one stop per row rather than five.
 *
 * **The quality and last-profile cells are the two that must not lie**, and both keep their
 * distinguishable absences rather than a zero: "Never scored" is not "Not computable", "Never
 * profiled" is not "Profiled empty", and a failed read says so rather than borrowing either. A
 * table that showed `0 rows` for an unprofiled dataset would report every unmeasured table as empty,
 * on the screen a steward uses to decide what to measure next.
 *
 * **The design's Kind column holds the dataset's state here.** Whether something is a table or a
 * view is not recorded by any harvest; whether it is still at the source is, and it is the fact a
 * reader would otherwise have to open the dataset to learn.
 */

function Breadcrumbs({ name }: { readonly name: string | null }): ReactElement {
  const { t } = useTranslation();
  return (
    <nav aria-label={t('breadcrumbs.label')} className="lq-breadcrumbs">
      <Link to={SCREEN_PATHS.datasets}>{t('catalogue.crumb.allSources')}</Link>
      <span aria-hidden="true" className="lq-breadcrumbs__sep">
        ›
      </span>
      {/* `aria-current="page"` rather than a link to here: a last crumb that navigates to the page
          you are on costs a screen-reader user a round trip for nothing. */}
      <span aria-current="page">{name ?? t('catalogue.source.unlisted')}</span>
    </nav>
  );
}

export function SourceDatasets(): ReactElement {
  const { t } = useTranslation();
  const { connectionId = '' } = useParams<{ connectionId: string }>();

  const connection = useConnection(connectionId);
  const summaries = useConnectionSummaries();
  const [search, setSearch] = useState('');
  const pager = useCursorStack();
  const query = useMemo(() => ({ search, connectionId }), [search, connectionId]);
  const datasets = usePagedDatasets(query, pager.cursor);

  const datasetIds = useMemo(
    () => (datasets.data?.items ?? []).map((each) => each.id),
    [datasets.data],
  );
  const scores = useLatestScores(datasetIds);
  /* One request for the page's profile facts rather than one per row. Both this and the scores read
     render as honest blanks while in flight, so the table appears as soon as the catalogue does. */
  const facts = useDatasetFacts(datasetIds);

  const summary = summaries.data?.items.find(
    (each) => each.connection_id === connectionId,
  );
  const searching = search.trim().length > 0;
  const now = new Date();

  function searchFor(next: string): void {
    setSearch(next);
    /* Required rather than tidy: a cursor is signed over a fingerprint of the query it was issued
       for, so carrying one across a search change is refused with a 400. */
    pager.reset();
  }

  /* "x of y" only when the page *is* the whole answer to the search: on a later page, or with
     another page behind this one, the shown figure would be one page's share and read as the
     number of matches. */
  const count =
    summary === undefined
      ? null
      : searching &&
          datasets.isSuccess &&
          pager.page === 1 &&
          datasets.data.nextCursor == null
        ? t('catalogue.grid.countOf', {
            shown: datasets.data.items.length,
            count: summary.datasets,
          })
        : t('catalogue.source.datasetCount', { count: summary.datasets });

  return (
    <section aria-labelledby="source-datasets-heading">
      <Breadcrumbs name={connection.data?.name ?? null} />

      {/* The design's header: the title on the left and the bare filter on the right. The two bulk
          actions sit beside the filter because both run against the *search*, so a reader who has
          filtered gets the datasets they can see. */}
      <PageHeader
        title={t('catalogue.title')}
        actions={
          <div className="lq-dsx__tools">
            <BulkAnalysisControl
              connectionId={connectionId}
              search={search}
              empty={datasets.isSuccess && datasets.data.items.length === 0}
            />
            <input
              type="search"
              className="lq-input lq-dsx__filter"
              aria-label={t('catalogue.grid.filterLabel')}
              placeholder={t('catalogue.grid.filterPlaceholder')}
              value={search}
              onChange={(event) => {
                searchFor(event.target.value);
              }}
            />
          </div>
        }
      />

      {datasets.isError ? (
        <Alert tone="error" title={t('catalog.unreachable')}>
          {datasets.error.message}
        </Alert>
      ) : null}

      <article className="lq-dsx__group">
        <div className="lq-dsx__groupHead">
          <h2 id="source-datasets-heading" className="lq-dsx__groupName">
            {connection.data?.name ?? t('catalogue.source.unlisted')}
          </h2>
          {connection.data !== undefined ? (
            <span className="lq-dsx__chip">
              {CONNECTOR_LABEL[connection.data.connector_type]}
            </span>
          ) : null}
          <span className="lq-dsx__groupFacts">
            {count ?? (
              <span className="lq-dim">
                {summaries.isError
                  ? t('catalogue.source.summaryUnread')
                  : t('catalogue.source.summaryLoading')}
              </span>
            )}
          </span>
        </div>

        {datasets.isPending ? (
          <div className="lq-dsx__groupBody">
            <p role="status" className="lq-muted">
              {t('catalog.loading')}
            </p>
            <SkeletonRows rows={8} columns={5} />
          </div>
        ) : null}

        {datasets.isSuccess && datasets.data.items.length === 0 ? (
          <p className="lq-dsx__empty">
            {searching
              ? t('catalogue.grid.noMatch')
              : connection.data !== undefined && connection.data.state !== 'verified'
                ? t('catalogue.grid.notVerified')
                : t('catalogue.grid.nothing')}
          </p>
        ) : null}

        {datasets.isSuccess && datasets.data.items.length > 0 ? (
          <div className="lq-dsx__scroll">
            <div
              className="lq-dsx__grid"
              role="table"
              aria-label={t('catalogue.source.tableCaption')}
            >
              <div className="lq-dsx__row lq-dsx__row--head" role="row">
                <span role="columnheader">{t('catalogue.grid.dataset')}</span>
                <span role="columnheader">{t('catalogue.grid.state')}</span>
                <span role="columnheader">{t('catalogue.grid.quality')}</span>
                <span role="columnheader">{t('catalogue.grid.lastProfile')}</span>
                <span role="columnheader">{t('catalogue.grid.coverage')}</span>
              </div>
              {datasets.data.items.map((dataset) => {
                const score = scores.data?.get(dataset.id);
                return (
                  <div key={dataset.id} className="lq-dsx__row" role="row">
                    <span role="cell" className="lq-dsx__nameCell">
                      <DatasetObjectTypeIcon objectType={dataset.object_type} />
                      <Link
                        className="lq-dsx__name"
                        to={`${SCREEN_PATHS.datasets}/${dataset.id}`}
                      >
                        {dataset.schema_name}.{dataset.object_name}
                      </Link>
                    </span>
                    <span
                      role="cell"
                      className="lq-dsx__state"
                      data-state={dataset.state}
                      title={t(`catalog.state.${dataset.state}.meaning`)}
                    >
                      {t(`catalog.state.${dataset.state}.label`)}
                    </span>
                    <span role="cell">
                      <QualityCell
                        summary={score}
                        pending={scores.isPending}
                        unavailable={scores.isError}
                      />
                    </span>
                    <span role="cell" className="lq-dsx__profile">
                      <LastProfileCell
                        reading={profileReading(facts.data?.get(dataset.id), {
                          /* Loading is not "unread": only a *failed* read is. */
                          unread: facts.isError,
                        })}
                        pending={facts.isPending}
                        now={now}
                      />
                    </span>
                    <span role="cell" className="lq-dsx__coverage">
                      {score === undefined ? null : (
                        <>
                          {t('catalogue.grid.columnsCovered', {
                            covered: score.columns_with_a_rule,
                            total: score.columns_total,
                          })}
                          {score.evidence_complete ? null : (
                            <span
                              className="lq-dsx__partial"
                              title={t('catalog.score.partial.meaning')}
                            >
                              {' '}
                              {t('catalogue.grid.partial')}
                            </span>
                          )}
                        </>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}
      </article>

      {datasets.isSuccess && (pager.canGoBack || datasets.data.nextCursor != null) ? (
        <div className="lq-pager">
          {/* The page number and never a total: a keyset listing cannot know how many pages exist
              without counting the whole table. Shown only when there is somewhere to go. */}
          <span>{t('catalog.pager.page', { page: pager.page })}</span>
          <span className="lq-pager__spacer" />
          <button
            type="button"
            className="lq-pager__btn"
            disabled={!pager.canGoBack}
            onClick={pager.back}
          >
            {t('catalog.pager.back')}
          </button>
          <button
            type="button"
            className="lq-pager__btn"
            disabled={datasets.data.nextCursor == null}
            onClick={() => {
              pager.forward(datasets.data.nextCursor);
            }}
          >
            {t('catalog.pager.next')}
          </button>
        </div>
      ) : null}
      {datasets.isSuccess &&
      datasets.data.items.length === DATASETS_PER_PAGE &&
      datasets.data.nextCursor == null ? (
        <p className="lq-muted">{t('catalog.pager.exactlyFull')}</p>
      ) : null}
    </section>
  );
}

const BAND_TONE = { ok: 'ok', warn: 'warn', danger: 'error' } as const;

/* The design's quality cell: the score in its band's colour and a bar beside it, or the words for
 * why there is no score. A failed read says so rather than claiming the dataset was never scored. */
function QualityCell({
  summary,
  pending,
  unavailable,
}: {
  readonly summary: ScoreSummary | undefined;
  readonly pending: boolean;
  readonly unavailable: boolean;
}): ReactElement {
  const { t } = useTranslation();
  if (pending) {
    return (
      <span className="lq-dim" aria-hidden="true">
        ·
      </span>
    );
  }
  if (unavailable) {
    return (
      <span className="lq-muted" title={t('catalog.score.unavailable.meaning')}>
        {t('catalog.score.unavailable.label')}
      </span>
    );
  }
  if (summary === undefined) {
    return (
      <span className="lq-muted" title={t('catalog.score.never.meaning')}>
        {t('catalogue.grid.neverScored')}
      </span>
    );
  }
  if (!summary.defensible || summary.value === null) {
    return (
      <span className="lq-muted" title={t('catalog.score.unmeasured.meaning')}>
        {t('catalogue.grid.notComputable')}
      </span>
    );
  }
  const tone = BAND_TONE[scoreBand(summary.value)];
  return (
    <span className="lq-dsx__quality" data-tone={tone}>
      <span className="lq-dsx__qualityValue lumiq-numeric">
        {summary.value.toFixed(1)}
      </span>
      <span className="lq-dsx__qualityBar" aria-hidden="true">
        <span
          className="lq-dsx__qualityFill"
          style={{ width: `${String(Math.max(0, Math.min(100, summary.value)))}%` }}
        />
      </span>
    </span>
  );
}

/* Rows the last profile read, compactly, and how long ago — or why there is no profile. */
function LastProfileCell({
  reading,
  pending,
  now,
}: {
  readonly reading: ProfileReading;
  readonly pending: boolean;
  readonly now: Date;
}): ReactElement {
  const { t } = useTranslation();
  if (pending) {
    return (
      <span className="lq-dim" aria-hidden="true">
        ·
      </span>
    );
  }
  if (reading.kind === 'unread') {
    return (
      <span className="lq-muted" title={t('catalogue.profile.unread.meaning')}>
        {t('catalogue.profile.unread.label')}
      </span>
    );
  }
  if (reading.kind === 'neverProfiled') {
    return (
      <span title={t('catalogue.profile.never.meaning')}>
        {t('catalogue.profile.never.label')}
      </span>
    );
  }
  const ago = ageText(t, reading.at, now);
  return (
    <>
      {reading.kind === 'profiledEmpty' ? (
        /* A profile that ran and reported no row count: not a count of zero, which the sketch
           store cannot tell apart from a missing figure. */
        <span title={t('catalogue.profile.empty.meaning')}>
          {t('catalogue.profile.empty.label')}
        </span>
      ) : reading.rows === 0 ? (
        <span title={t('catalogue.profile.rowsMeaning')}>
          {t('catalogue.grid.profiledEmpty')}
        </span>
      ) : (
        <span title={t('catalogue.profile.rowsMeaning')}>
          {t('catalogue.grid.rows', {
            count: reading.rows,
            rows: compactNumber(reading.rows),
          })}
        </span>
      )}
      {ago === null ? null : <span className="lq-dsx__ago"> {ago}</span>}
    </>
  );
}
